/**
 * Referral (09) data layer — the ONLY place this feature talks to the server. Every path + capability
 * below is code-verified against server/src/modules/referral/{routes,controller,validators}.js. Server
 * data lives only in TanStack Query; mutations invalidate the affected lab-scoped keys.
 *
 * CLOUD-ONLY (D-28). On a LOCAL install the referral router is NOT mounted, so every /referral/* path
 * 404s. The screens detect that via `isModuleUnmounted(error)` (httpStatus 404 / NOT_FOUND) and render
 * the "indisponible sur cette installation" state instead of a hard error — the module degrades cleanly
 * rather than appearing broken.
 *
 * Endpoints consumed (all under /api/v1):
 *   GET  /referral/analytics             referral.analytics.read   (roster + link table + tiles)
 *   GET  /referral/retours?medecin_id    referral.retour.read
 *   GET  /referral/candidate?medecin_id  referral.remise.set
 *   POST /referral/medecins/inviter      referral.lien.manage
 *   POST /referral/liens/decision        referral.lien.manage
 *   POST /referral/liens/:id/revoquer    referral.lien.manage
 *   POST /referral/codes                 referral.code.manage
 *   POST /referral/codes/:id/toggle      referral.code.manage
 *
 * NOTE there is no GET-liens / GET-codes / GET-medecins endpoint: the per-médecin link status + the
 * roster both come from the analytics read (each card carries `lien_statut` + `retours_count`). The
 * analytics roster is the join root for the link table, the code editor, and the returns drill-down.
 */
import { ApiError, api, qk, useApiMutation, useApiQuery } from "@/api";
import { useAuthStore } from "@/auth";
import type {
  AnalyticsCards,
  CandidateResponse,
  CodeCreated,
  CodeToggled,
  DecisionResult,
  Id,
  InviteResult,
  RetourPrescripteur,
  RevokeResult,
} from "./types";

/** Lab id for query scoping — from the live auth session (multi-tenant cache isolation). */
export function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? s.lab?.id ?? null);
}

/** Lab-scoped query-key sub-factory for the referral feature. */
export const referralKeys = {
  analytics: (labId: string | null) => qk.scoped(labId, "referral", "analytics"),
  retours: (labId: string | null, medecinId: string) =>
    qk.scoped(labId, "referral", "retours", medecinId),
  candidate: (labId: string | null, medecinId: string) =>
    qk.scoped(labId, "referral", "candidate", medecinId),
};

/**
 * True when an error means the CLOUD-only referral module is not mounted on this install (its /api/v1
 * routes simply do not exist → 404 / NOT_FOUND). The screens swap to the "indisponible" state on this,
 * never to the generic ErrorState.
 */
export function isModuleUnmounted(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.httpStatus === 404 || error.code === "NOT_FOUND";
}

// ── Queries ──────────────────────────────────────────────────────────────────────

/**
 * The per-médecin analytics roster — the join root. Drives the link table (each card's lien_statut),
 * the analytics tiles (retours_count), and the code editor's médecin picker. Also the module-mounted
 * probe: a 404 here means referral is unmounted on a local install (handled by isModuleUnmounted).
 */
export function useAnalytics(labId: string | null, enabled: boolean) {
  return useApiQuery<AnalyticsCards>(referralKeys.analytics(labId), "/referral/analytics", {
    enabled,
    staleTime: 30_000,
  });
}

/** The prescriber-return tracking rows for one médecin (en_attente / envoye / echec). */
export function useRetours(labId: string | null, medecinId: string | null, enabled: boolean) {
  return useApiQuery<RetourPrescripteur[]>(
    referralKeys.retours(labId, medecinId ?? "_"),
    "/referral/retours",
    {
      enabled: enabled && !!medecinId,
      ...(medecinId ? { query: { medecin_id: medecinId } } : {}),
      staleTime: 15_000,
    },
  );
}

/** The live discount candidate for a médecin's default code (REF-2), or null (clean absence). */
export function useCandidate(labId: string | null, medecinId: string | null, enabled: boolean) {
  return useApiQuery<CandidateResponse>(
    referralKeys.candidate(labId, medecinId ?? "_"),
    "/referral/candidate",
    {
      enabled: enabled && !!medecinId,
      ...(medecinId ? { query: { medecin_id: medecinId } } : {}),
      staleTime: 30_000,
    },
  );
}

// ── Lien mutations (invite → decision → revoke) ─────────────────────────────────────

export interface InviteVars {
  nom: string;
  prenom?: string;
  specialite?: string;
  novera_email: string;
  remise_pct?: number;
}

/** POST /referral/medecins/inviter — open an `invite` lien (returns the seam-2 callback token). */
export function useInviteMedecin(labId: string | null) {
  return useApiMutation<InviteResult, InviteVars>(
    (vars) => {
      const body: Record<string, unknown> = {
        nom: vars.nom,
        novera_email: vars.novera_email,
      };
      if (vars.prenom) body.prenom = vars.prenom;
      if (vars.specialite) body.specialite = vars.specialite;
      if (vars.remise_pct !== undefined) body.remise_pct = vars.remise_pct;
      return api.post<InviteResult>("/referral/medecins/inviter", body);
    },
    { invalidate: [referralKeys.analytics(labId)] },
  );
}

export interface DecisionVars {
  callback_token: string;
  decision: "APPROVED" | "REFUSED";
  novera_ref?: string;
}

/**
 * POST /referral/liens/decision — apply the seam-2 callback decision (single-use token). Normally the
 * central Novera MED platform calls this; the screen exposes it so an operator can resolve a pending
 * invite from the callback token when wiring/testing the seam.
 */
export function useApplyDecision(labId: string | null) {
  return useApiMutation<DecisionResult, DecisionVars>(
    (vars) => {
      const body: Record<string, unknown> = {
        callback_token: vars.callback_token,
        decision: vars.decision,
      };
      if (vars.novera_ref) body.medecin = { novera_ref: vars.novera_ref };
      return api.post<DecisionResult>("/referral/liens/decision", body);
    },
    { invalidate: [referralKeys.analytics(labId)] },
  );
}

export interface RevokeVars {
  lienId: string;
  motif?: string;
}

/** POST /referral/liens/:id/revoquer — archive an approuve lien + cascade its codes → inactif. */
export function useRevokeLien(labId: string | null) {
  return useApiMutation<RevokeResult, RevokeVars>(
    ({ lienId, motif }) =>
      api.post<RevokeResult>(`/referral/liens/${lienId}/revoquer`, motif ? { motif } : {}),
    { invalidate: [referralKeys.analytics(labId)] },
  );
}

// ── Code mutations (CodeReferral CRUD) ──────────────────────────────────────────────

export interface CreateCodeVars {
  medecin_id: Id;
  code: string;
  libelle?: string;
  remise_pct?: number;
  expire_at?: string;
}

/** POST /referral/codes — create a referral code (requires an approuve lien; 409 otherwise). */
export function useCreateCode(labId: string | null) {
  return useApiMutation<CodeCreated, CreateCodeVars>(
    (vars) => {
      const body: Record<string, unknown> = { medecin_id: vars.medecin_id, code: vars.code };
      if (vars.libelle) body.libelle = vars.libelle;
      if (vars.remise_pct !== undefined) body.remise_pct = vars.remise_pct;
      if (vars.expire_at) body.expire_at = vars.expire_at;
      return api.post<CodeCreated>("/referral/codes", body);
    },
    { invalidate: [referralKeys.analytics(labId)] },
  );
}

export interface ToggleCodeVars {
  codeId: string;
  statut: "actif" | "inactif";
}

/** POST /referral/codes/:id/toggle — actif ↔ inactif (full CRUD by reissue, L-1; reversible). */
export function useToggleCode(labId: string | null) {
  return useApiMutation<CodeToggled, ToggleCodeVars>(
    ({ codeId, statut }) =>
      api.post<CodeToggled>(`/referral/codes/${codeId}/toggle`, { statut }),
    { invalidate: [referralKeys.analytics(labId)] },
  );
}
